import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["header", "body", "popup", "popupList", "emptyCell"]
  static values  = { storageKey: String, defaultOrder: Array }

  connect() {
    this.boundDragHeaders = new WeakSet()
    this.boundDocClick = this.onDocClick.bind(this)
    this.boundReposition = this.repositionPopup.bind(this)
    this.boundScroll = this.onScroll.bind(this)
    this.boundReapplyAfterMorph = this.reapplyAfterMorph.bind(this)
    document.addEventListener("click", this.boundDocClick)
    document.addEventListener("turbo:morph", this.boundReapplyAfterMorph)

    this.applyClientState()
  }

  disconnect() {
    if (this.boundDocClick) document.removeEventListener("click", this.boundDocClick)
    if (this.boundReapplyAfterMorph) document.removeEventListener("turbo:morph", this.boundReapplyAfterMorph)
    this.stopTrackingPopup()
  }

  readHeaderMetadata() {
    if (!this.hasHeaderTarget) return false
    this.headerRow = this.headerTargets[0].parentElement
    if (!this.headerRow) return false

    this.columns = this.headerTargets.map(th => ({
      id:            th.dataset.columnId,
      locked:        th.dataset.columnLocked === "true",
      defaultHidden: th.dataset.columnDefaultHidden === "true",
      label:         this.thLabel(th)
    }))
    return true
  }

  reapplyAfterMorph() {
    this.applyClientState()
    this.syncPopupTriggerAriaExpanded()
  }

  syncPopupTriggerAriaExpanded() {
    if (!this.popupTrigger) return
    const isOpen = this.hasPopupTarget && !this.popupTarget.classList.contains("hidden")
    this.popupTrigger.setAttribute("aria-expanded", isOpen ? "true" : "false")
  }

  applyClientState() {
    if (!this.readHeaderMetadata()) return false
    this.applyState(this.readState())
    this.buildPopupList()
    this.bindDragHandlers()
    return true
  }

  columnById(id) {
    return this.columns.find(c => c.id === id)
  }

  cookieName() {
    return this.storageKeyValue || "cup_cols_default"
  }

  readState() {
    const blank = { order: [], hidden: [], shown: [] }
    const raw = this.readCookie(this.cookieName())
    if (!raw) return blank
    try {
      const parsed = JSON.parse(raw)
      return {
        order:  Array.isArray(parsed.order)  ? parsed.order  : [],
        hidden: Array.isArray(parsed.hidden) ? parsed.hidden : [],
        shown:  Array.isArray(parsed.shown)  ? parsed.shown  : []
      }
    } catch (_e) {
      return blank
    }
  }

  writeState(state) {
    const value = encodeURIComponent(JSON.stringify(state))
    const thirtyDays = 60 * 60 * 24 * 30
    document.cookie = `${this.cookieName()}=${value}; path=/; max-age=${thirtyDays}; samesite=lax`
  }

  usingDeclaredDefaultOrder() {
    return this.defaultOrderValue?.length > 0
  }

  currentState() {
    const order  = this.usingDeclaredDefaultOrder() ? [] : this.movableColumnIds()
    const hidden = this.columns.filter(c => !c.locked && !c.defaultHidden && this.isHidden(c.id)).map(c => c.id)
    const shown  = this.columns.filter(c => !c.locked && c.defaultHidden && !this.isHidden(c.id)).map(c => c.id)
    return { order, hidden, shown }
  }

  persist() {
    this.writeState(this.currentState())
  }

  reset() {
    document.cookie = `${this.cookieName()}=; path=/; max-age=0`
    window.location.reload()
  }

  toggleHidden(id) {
    if (this.columnById(id)?.locked) return
    this.setHidden(id, !this.isHidden(id))
    this.adjustEmptyCell()
    this.persist()
  }

  applyState(state) {
    const knownIds = new Set(this.columns.map(c => c.id))
    const domOrder = this.columns.filter(c => !c.locked).map(c => c.id)
    const explicit = (this.defaultOrderValue || [])
      .filter(id => knownIds.has(id) && !this.columnById(id)?.locked)
    const movableDefault = explicit.length > 0
      ? [...explicit, ...domOrder.filter(id => !explicit.includes(id))]
      : domOrder
    const stateOrder = explicit.length > 0
      ? []
      : state.order.filter(id => knownIds.has(id) && !this.columnById(id).locked)
    const missing    = movableDefault.filter(id => !stateOrder.includes(id))
    const targetMovableOrder = [...stateOrder, ...missing]

    this.reorderMovables(targetMovableOrder)

    const hiddenIds = new Set(state.hidden.filter(id => knownIds.has(id) && !this.columnById(id).locked))
    const shownIds  = new Set(state.shown.filter(id => knownIds.has(id) && !this.columnById(id).locked))
    this.columns.forEach(c => {
      if (c.locked) return
      const hide = c.defaultHidden ? !shownIds.has(c.id) : hiddenIds.has(c.id)
      this.setHidden(c.id, hide)
    })
    this.adjustEmptyCell()
  }

  reorderMovables(targetMovableOrder) {
    const rows = [this.headerRow, ...this.bodyRows()]
    rows.forEach(row => this.reorderRowMovables(row, targetMovableOrder))
  }

  reorderRowMovables(row, targetMovableOrder) {
    const cells = Array.from(row.querySelectorAll(":scope > th[data-column-id], :scope > td[data-column-id]"))
    if (cells.length === 0) return

    const isMovable = (cell) =>
      cell.dataset.columnLocked !== "true" && !this.columnById(cell.dataset.columnId)?.locked

    const movableByCol = {}
    cells.filter(isMovable).forEach(cell => { movableByCol[cell.dataset.columnId] = cell })

    targetMovableOrder.forEach((id, i) => {
      const cell         = movableByCol[id]
      const liveSlotNode = Array.from(row.children).filter(isMovable)[i]
      if (cell && liveSlotNode && cell !== liveSlotNode) {
        row.insertBefore(cell, liveSlotNode)
      }
    })
  }

  setHidden(colId, hide) {
    const cells = this.element.querySelectorAll(`[data-column-id="${colId}"]`)
    cells.forEach(cell => {
      if (hide) cell.style.display = "none"
      else      cell.style.removeProperty("display")
    })
  }

  adjustEmptyCell() {
    if (!this.hasEmptyCellTarget || !this.headerRow) return
    const visible = Array.from(this.headerRow.children)
      .filter(th => th.style.display !== "none").length
    this.emptyCellTarget.colSpan = visible
  }

  isHidden(colId) {
    const th = this.headerRow.querySelector(`th[data-column-id="${colId}"]`)
    return th ? th.style.display === "none" : false
  }

  movableColumnIds() {
    return Array.from(this.headerRow.querySelectorAll("th[data-column-id]"))
      .filter(th => th.dataset.columnLocked !== "true")
      .map(th => th.dataset.columnId)
  }

  bodyRows() {
    if (!this.hasBodyTarget) return []
    return Array.from(this.bodyTarget.querySelectorAll(":scope > tr"))
  }

  thLabel(th) {
    const explicit = th.dataset.columnLabel
    if (explicit) return explicit
    const text = th.textContent.trim().replace(/[↕↑↓]/g, "").trim()
    return text || th.dataset.columnId
  }

  togglePopup(e) {
    e.preventDefault()
    e.stopPropagation()
    if (!this.hasPopupTarget) return
    if (this.popupTarget.classList.contains("hidden")) this.showPopup(e.currentTarget)
    else this.hidePopup()
  }

  showPopup(trigger) {
    this.popupTrigger = trigger
    this.popupTarget.classList.remove("hidden")
    trigger.setAttribute("aria-expanded", "true")
    this.repositionPopup()
    this.startTrackingPopup()
    this.popupTarget.querySelector("input[type='checkbox']")?.focus()
  }

  hidePopup() {
    if (!this.hasPopupTarget) return
    if (this.popupTarget.classList.contains("hidden")) return
    const refocus = this.popupTarget.contains(document.activeElement)
    this.popupTarget.classList.add("hidden")
    this.popupTrigger?.setAttribute("aria-expanded", "false")
    this.stopTrackingPopup()
    if (refocus) this.popupTrigger?.focus()
  }

  triggerScrolledOutOfView(anchor) {
    return anchor.bottom < 0 || anchor.top > window.innerHeight
  }

  repositionPopup() {
    if (!this.hasPopupTarget || !this.popupTrigger) return
    const popup = this.popupTarget
    const margin = 8
    const gap = 4
    const anchor = this.popupTrigger.getBoundingClientRect()

    if (this.triggerScrolledOutOfView(anchor)) return this.hidePopup()

    const scrollTopBeforeMeasuring = popup.scrollTop
    popup.style.maxHeight = ""
    const height = popup.offsetHeight

    const main = document.querySelector("main")?.getBoundingClientRect()
    const ceiling = Math.max(margin, main ? main.top + margin : margin)
    const floor = Math.min(window.innerHeight - margin, main ? main.bottom - margin : window.innerHeight - margin)

    const roomBelow = floor - anchor.bottom - gap
    const roomAbove = anchor.top - gap - ceiling
    const above = height > roomBelow && roomAbove > roomBelow
    const room = Math.max(above ? roomAbove : roomBelow, 0)
    popup.style.maxHeight = `${room}px`
    popup.scrollTop = scrollTopBeforeMeasuring

    const widthAfterClamping = popup.offsetWidth
    const shown = Math.min(height, room)
    const top = above ? anchor.top - gap - shown : anchor.bottom + gap
    const left = Math.min(Math.max(margin, anchor.right - widthAfterClamping), window.innerWidth - widthAfterClamping - margin)
    popup.style.top = `${Math.max(ceiling, top)}px`
    popup.style.left = `${Math.max(margin, left)}px`
  }

  startTrackingPopup() {
    window.addEventListener("scroll", this.boundScroll, true)
    window.addEventListener("resize", this.boundReposition)
  }

  stopTrackingPopup() {
    if (!this.boundReposition) return
    window.removeEventListener("scroll", this.boundScroll, true)
    window.removeEventListener("resize", this.boundReposition)
  }

  scrollOriginatedInsidePopup(e) {
    return e.target?.nodeType && this.popupTarget.contains(e.target)
  }

  onScroll(e) {
    if (this.scrollOriginatedInsidePopup(e)) return
    this.repositionPopup()
  }

  onDocClick(e) {
    if (!this.hasPopupTarget) return
    if (this.popupTarget.classList.contains("hidden")) return
    if (this.popupTarget.contains(e.target)) return
    if (e.target.closest("[data-action*='columns#togglePopup']")) return
    this.hidePopup()
  }

  buildPopupList() {
    if (!this.hasPopupListTarget) return
    this.popupListTarget.innerHTML = ""
    this.columns.filter(c => !c.locked).forEach(col => {
      const label = document.createElement("label")
      label.className = "flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50 cursor-pointer dark:text-zinc-200 dark:hover:bg-zinc-800/60 whitespace-nowrap"
      const cb = document.createElement("input")
      cb.type = "checkbox"
      cb.checked = !this.isHidden(col.id)
      cb.className = "h-4 w-4 rounded border-zinc-300 text-orange-600 focus:ring-orange-500 dark:border-zinc-600 dark:bg-zinc-800"
      cb.addEventListener("change", () => {
        this.setHidden(col.id, !cb.checked)
        this.adjustEmptyCell()
        this.persist()
      })
      const span = document.createElement("span")
      span.textContent = col.label
      label.appendChild(cb)
      label.appendChild(span)
      this.popupListTarget.appendChild(label)
    })
  }

  resetAction(e) {
    e.preventDefault()
    this.reset()
  }

  bindDragHandlers() {
    this.headerTargets.forEach(th => {
      if (th.dataset.columnLocked === "true") return
      th.setAttribute("draggable", "true")
      th.classList.add("cursor-move")
      if (this.boundDragHeaders.has(th)) return
      this.boundDragHeaders.add(th)
      th.addEventListener("dragstart", this.onDragStart.bind(this))
      th.addEventListener("dragover",  this.onDragOver.bind(this))
      th.addEventListener("dragleave", this.onDragLeave.bind(this))
      th.addEventListener("drop",      this.onDrop.bind(this))
      th.addEventListener("dragend",   this.onDragEnd.bind(this))
    })
  }

  startedInsideColumnFilter(e) {
    return e.target.closest("[data-controller~='column-filter']") !== null
  }

  onDragStart(e) {
    if (this.startedInsideColumnFilter(e)) {
      e.preventDefault()
      return
    }

    const th = e.currentTarget
    const colId = th.dataset.columnId
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", colId)
    this.dragSourceId = colId
    th.classList.add("opacity-40")
  }

  onDragOver(e) {
    if (!this.dragSourceId) return
    const th = e.currentTarget
    if (th.dataset.columnId === this.dragSourceId) return
    if (th.dataset.columnLocked === "true") return
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    th.classList.add("ring-2", "ring-orange-400", "ring-inset")
  }

  onDragLeave(e) {
    e.currentTarget.classList.remove("ring-2", "ring-orange-400", "ring-inset")
  }

  onDrop(e) {
    e.preventDefault()
    const target = e.currentTarget
    target.classList.remove("ring-2", "ring-orange-400", "ring-inset")
    const sourceId = this.dragSourceId
    const targetId = target.dataset.columnId
    if (!sourceId || !targetId || sourceId === targetId) return
    if (target.dataset.columnLocked === "true") return

    const order   = this.movableColumnIds()
    const fromIdx = order.indexOf(sourceId)
    const toIdx   = order.indexOf(targetId)
    if (fromIdx < 0 || toIdx < 0) return
    order.splice(fromIdx, 1)
    const insertAt = order.indexOf(targetId) + (fromIdx < toIdx ? 1 : 0)
    order.splice(insertAt, 0, sourceId)

    this.reorderMovables(order)
    this.persist()
  }

  onDragEnd(_e) {
    this.dragSourceId = null
    this.headerTargets.forEach(th => {
      th.classList.remove("opacity-40", "ring-2", "ring-orange-400", "ring-inset")
    })
  }

  readCookie(name) {
    const match = document.cookie.split("; ").find(c => c.startsWith(name + "="))
    if (!match) return null
    try {
      return decodeURIComponent(match.split("=").slice(1).join("="))
    } catch (_e) {
      return null
    }
  }
}
